import { AlertTriangle, X, Check, Loader2 } from 'lucide-react'
import { useEffect } from 'react'

/**
 * ConfirmThresholdModal — Modal de confirmación DEC-024 capa 3.
 *
 * Se renderiza cuando el backend devuelve `requires_confirmation: true`
 * en el estimate. El usuario ve un resumen del job + acepta/cancela:
 *   - Cancelar → cierra modal sin lanzar search.
 *   - Confirmar → re-llama `executeSearch` con header `X-User-Confirmed: true`.
 *
 * **Filosofía DEC-024 capa 3**: jobs grandes (≥ `single_job_confirm_threshold`
 * del plan) requieren un click extra del usuario para evitar consumos
 * masivos no intencionados de cupo. El threshold es informativo:
 *   - Free: 15 leads (todo el cupo mensual).
 *   - Starter: 50 leads.
 *   - Pro: 200 leads.
 *
 * **Accesibilidad**: dialog modal con role=dialog + aria-modal=true +
 * focus trap (browser nativo con `<dialog>` tag) + cierre con Escape.
 */
export interface ConfirmThresholdModalProps {
  open: boolean
  /** Leads que se consumirían si el usuario confirma. */
  leadsToConsume: number
  /** Threshold del plan que disparó la confirmación (informativo). */
  confirmThreshold: number | null
  /** Cupo restante TRAS consumir (informativo). */
  leadsRemainingAfter: number
  /** Plan del usuario (free/starter/pro). */
  planCode: string
  /** True mientras se está enviando la confirmación al backend. */
  loading?: boolean
  onCancel: () => void
  onConfirm: () => void
}

export function ConfirmThresholdModal({
  open,
  leadsToConsume,
  confirmThreshold,
  leadsRemainingAfter,
  planCode,
  loading = false,
  onCancel,
  onConfirm,
}: ConfirmThresholdModalProps) {
  // Cierre con Escape.
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) onCancel()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, loading, onCancel])

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-threshold-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 px-4"
      onClick={loading ? undefined : onCancel}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="w-full max-w-md rounded-lg bg-white shadow-xl"
      >
        {/* Header */}
        <div className="flex items-start gap-3 border-b border-gray-100 px-5 py-4">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
            <AlertTriangle size={18} className="text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2
              id="confirm-threshold-title"
              className="text-base font-semibold text-gray-900"
            >
              Confirma la extracción
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">
              Este trabajo consumirá una parte significativa de tu cupo.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            aria-label="Cerrar"
            className="flex h-7 w-7 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4">
          <dl className="space-y-3 text-sm">
            <div className="flex items-baseline justify-between">
              <dt className="text-gray-600">Leads a extraer</dt>
              <dd className="font-semibold text-gray-900">
                {leadsToConsume.toLocaleString('es-ES')}
              </dd>
            </div>
            {confirmThreshold !== null && (
              <div className="flex items-baseline justify-between">
                <dt className="text-gray-600">
                  Umbral de tu plan ({planCode})
                </dt>
                <dd className="text-gray-700">
                  ≥ {confirmThreshold.toLocaleString('es-ES')}
                </dd>
              </div>
            )}
            <div className="flex items-baseline justify-between border-t border-gray-100 pt-3">
              <dt className="text-gray-600">Cupo restante tras extraer</dt>
              <dd
                className={`font-semibold ${
                  leadsRemainingAfter < 0
                    ? 'text-red-600'
                    : leadsRemainingAfter < 10
                      ? 'text-amber-600'
                      : 'text-gray-900'
                }`}
              >
                {leadsRemainingAfter.toLocaleString('es-ES')}
              </dd>
            </div>
          </dl>

          <div className="mt-4 rounded-md border border-amber-100 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
            <p className="font-medium">¿Por qué pedimos confirmación?</p>
            <p className="mt-1 text-amber-700">
              Tu plan {planCode} requiere confirmación explícita para trabajos
              de este tamaño. Te avisamos para evitar consumos accidentales.
              Puedes cambiar a un plan mayor para extracciones automáticas
              más grandes.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Extrayendo…
              </>
            ) : (
              <>
                <Check size={14} />
                Confirmar y extraer
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
